import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DocIcon } from './DocIcons';
import { docsApi, errorOf } from '../../lib/docs';
import { useFeature } from '../../lib/features';
import './Docs.css';

/**
 * Documentation on a ticket: the articles attached to it, and suggestions
 * from its category - so whoever picks up the job has the runbook beside it.
 * Staff attach and detach; a contractor on the ticket sees what is attached
 * and that they may read (the server leaves out anything they may not).
 * Draws nothing at all when Documentation is off or not set up, or when there
 * is nothing to show a reader who cannot attach.
 */
export default function TicketDocs({ ticketId }) {
  const on = useFeature('docs');
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [err, setErr] = useState(null);
  // Only an answer of the right shape is drawn: anything else (an older
  // server, a stand-in) is treated as nothing to show, never as a crash on
  // the ticket page around it.
  const load = useCallback(() => docsApi.get(`/tickets/${ticketId}/docs`)
    .then((r) => setData(r && Array.isArray(r.linked) && Array.isArray(r.suggested) ? r : null))
    .catch(() => setData(null)), [ticketId]);
  useEffect(() => { if (on && ticketId) load(); }, [on, ticketId, load]);
  useEffect(() => {
    if (!q.trim() || !data?.can_link) { setFound([]); return undefined; }
    let live = true;
    const t = setTimeout(() => docsApi.get('/docs/link-targets', { q }).then((r) => { if (live) setFound(r.articles.slice(0, 6)); }).catch(() => {}), 200);
    return () => { live = false; clearTimeout(t); };
  }, [q, data?.can_link]);
  if (!on || !data) return null;
  if (!data.can_link && !data.linked.length) return null;
  const attach = async (id) => { setErr(null); try { await docsApi.post(`/tickets/${ticketId}/docs`, { node_id: id }); setQ(''); load(); } catch (e) { setErr(errorOf(e)); } };
  const detach = async (id) => { setErr(null); try { await docsApi.del(`/tickets/${ticketId}/docs/${id}`); load(); } catch (e) { setErr(errorOf(e)); } };
  return (
    <section className="tdocs" aria-label="Documentation">
      <h3 className="tdocs-title"><DocIcon name="book" />Documentation</h3>
      {data.linked.length > 0 ? (
        <ul className="tdocs-list">
          {data.linked.map((a) => (
            <li key={a.id}>
              <Link to={`/docs/${a.id}`} className="dm-chip dm-chip-doc"><DocIcon name={a.article_type} />{a.title}</Link>
              {data.can_link && <button type="button" className="btn btn-plain btn-small" aria-label={`Detach ${a.title}`} onClick={() => detach(a.id)}><DocIcon name="close" /></button>}
            </li>
          ))}
        </ul>
      ) : <p className="dp-hint">No documentation attached.</p>}
      {data.can_link && data.suggested.length > 0 && (
        <div className="tdocs-sugg">
          <span className="dp-label">Suggested{data.category ? ` for ${data.category}` : ''}</span>
          <ul className="tdocs-list">
            {data.suggested.map((a) => (
              <li key={a.id}>
                <Link to={`/docs/${a.id}`} className="dm-chip is-plain"><DocIcon name={a.article_type} />{a.title}</Link>
                <button type="button" className="btn btn-plain btn-small" onClick={() => attach(a.id)}>Attach</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.can_link && (
        <div className="tdocs-find">
          <input type="search" className="de-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Attach a doc - search by title" aria-label="Find a doc to attach" />
          {found.length > 0 && (
            <ul className="tdocs-found">
              {found.map((a) => <li key={a.id}><button type="button" onClick={() => attach(a.id)}><DocIcon name={a.article_type} />{a.title}<small>{(a.path || []).join(' › ')}</small></button></li>)}
            </ul>
          )}
        </div>
      )}
      {err && <p className="dp-error" role="alert">{err}</p>}
    </section>
  );
}
